import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, X, Store, Package, Building2 } from 'lucide-react';
import api from '@/lib/api';

export function GlobalSearch() {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState({ aptekas: [], products: [], companies: [] });
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (!query || query.length < 2) { setResults({ aptekas: [], products: [], companies: [] }); return; }
    const timer = setTimeout(async () => {
      setLoading(true);
      try {
        const [aptRes, prodRes, compRes] = await Promise.all([
          api.get('/aptekas', { params: { search: query } }).catch(() => ({ data: [] })),
          api.get('/products', { params: { search: query } }).catch(() => ({ data: [] })),
          api.get('/companies', { params: { search: query } }).catch(() => ({ data: [] })),
        ]);
        setResults({
          aptekas: (aptRes.data || []).filter(a => a.name?.toLowerCase().includes(query.toLowerCase())).slice(0, 5),
          products: (prodRes.data || []).filter(p => p.name?.toLowerCase().includes(query.toLowerCase())).slice(0, 5),
          companies: (compRes.data || []).filter(c => c.name?.toLowerCase().includes(query.toLowerCase())).slice(0, 5),
        });
      } catch { /* ignore */ }
      setLoading(false);
    }, 300);
    return () => clearTimeout(timer);
  }, [query]);

  const handleSelect = (type, item) => {
    setOpen(false);
    setQuery('');
    if (type === 'apteka') navigate('/apteka-control');
    else if (type === 'product') navigate('/products');
    else if (type === 'company') navigate('/catalog');
  };

  const totalResults = results.aptekas.length + results.products.length + results.companies.length;

  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        className="flex items-center gap-2 px-3 py-1.5 rounded-lg border bg-muted/50 hover:bg-muted text-muted-foreground text-sm transition-colors"
        data-testid="global-search-trigger"
      >
        <Search className="w-3.5 h-3.5" />
        <span className="hidden sm:inline text-xs">Search...</span>
        <kbd className="hidden md:inline text-[10px] bg-background border rounded px-1.5 py-0.5 font-mono">Ctrl+K</kbd>
      </button>
    );
  }

  return (
    <>
      <div className="fixed inset-0 bg-black/40 z-50" onClick={() => { setOpen(false); setQuery(''); }} />
      <div className="fixed top-[10%] left-1/2 -translate-x-1/2 w-[90%] max-w-lg z-50 bg-card rounded-xl border shadow-2xl overflow-hidden" data-testid="global-search-modal">
        <div className="flex items-center gap-2 px-4 py-3 border-b">
          <Search className="w-4 h-4 text-muted-foreground shrink-0" />
          <input
            autoFocus
            className="flex-1 bg-transparent outline-none text-sm"
            placeholder="Search aptekas, products, companies..."
            value={query}
            onChange={e => setQuery(e.target.value)}
            data-testid="global-search-input"
          />
          <button onClick={() => { setOpen(false); setQuery(''); }} className="text-muted-foreground hover:text-foreground">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="max-h-[400px] overflow-y-auto">
          {loading && <div className="px-4 py-6 text-center text-sm text-muted-foreground">Searching...</div>}
          {!loading && query.length >= 2 && totalResults === 0 && (
            <div className="px-4 py-6 text-center text-sm text-muted-foreground">No results found</div>
          )}
          {!loading && query.length < 2 && (
            <div className="px-4 py-6 text-center text-sm text-muted-foreground">Type at least 2 characters to search</div>
          )}
          {results.aptekas.length > 0 && (
            <div>
              <div className="px-4 py-1.5 text-[10px] font-bold uppercase tracking-wider text-muted-foreground bg-muted/30">Pharmacies</div>
              {results.aptekas.map(a => (
                <button key={a.apteka_id} className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-accent transition-colors" onClick={() => handleSelect('apteka', a)} data-testid={`search-result-apteka-${a.apteka_id}`}>
                  <Store className="w-4 h-4 text-muted-foreground shrink-0" />
                  <div className="min-w-0"><p className="text-sm font-medium truncate">{a.name}</p><p className="text-xs text-muted-foreground truncate">{a.district || a.state || ''}</p></div>
                </button>
              ))}
            </div>
          )}
          {results.products.length > 0 && (
            <div>
              <div className="px-4 py-1.5 text-[10px] font-bold uppercase tracking-wider text-muted-foreground bg-muted/30">Products</div>
              {results.products.map(p => (
                <button key={p.product_id} className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-accent transition-colors" onClick={() => handleSelect('product', p)} data-testid={`search-result-product-${p.product_id}`}>
                  <Package className="w-4 h-4 text-muted-foreground shrink-0" />
                  <div className="min-w-0"><p className="text-sm font-medium truncate">{p.name}</p><p className="text-xs text-muted-foreground truncate">{p.category || ''}</p></div>
                </button>
              ))}
            </div>
          )}
          {results.companies.length > 0 && (
            <div>
              <div className="px-4 py-1.5 text-[10px] font-bold uppercase tracking-wider text-muted-foreground bg-muted/30">Companies</div>
              {results.companies.map(c => (
                <button key={c.company_id} className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-accent transition-colors" onClick={() => handleSelect('company', c)} data-testid={`search-result-company-${c.company_id}`}>
                  <Building2 className="w-4 h-4 text-muted-foreground shrink-0" />
                  <div className="min-w-0"><p className="text-sm font-medium truncate">{c.name}</p><p className="text-xs text-muted-foreground truncate">{c.mode || ''}</p></div>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  );
}
